import { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import {
  Plus, Trash2, Copy, ArrowUp, ArrowDown, Monitor, Smartphone, Sparkles, Loader2, GripVertical, Layout,
} from "lucide-react";
import { ColorField } from "@/components/studio/DrawToolbar";
import { SiteSectionView } from "@/components/studio/SitePreview";

let sid = 0;
const nextId = () => `s${Date.now().toString(36)}${(sid++).toString(36)}`;

const BLANK = {
  heading: "", body: "", imageId: null, alt: "", license: "", credit: "", quality: "full",
  videoId: null, buttonText: "", cards: [{ title: "", body: "" }, { title: "", body: "" }, { title: "", body: "" }],
  careerName: "", wage: "", education: "", skills: "",
};

const PRESET = {
  nav: { heading: "My Site" }, hero: { heading: "", body: "" }, text: { heading: "Section title", body: "" },
  cards: { heading: "Features" }, image: { heading: "Image" }, gallery: { heading: "Gallery" },
  video: { heading: "Watch this" }, cta: { heading: "Ready to start?", buttonText: "" },
  career: { heading: "Career profile" }, footer: { body: "© My Site" },
};

const MEDIA = (t) => t === "image" || t === "gallery";

export default function SiteEditorCore({ doc, setDoc, config, pageRef }) {
  const sections = doc.sections || [];
  const site = doc.site || { theme: {}, navLinks: [] };
  const theme = site.theme || {};
  const [selId, setSelId] = useState(null);
  const [dragId, setDragId] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const mobile = doc.preview === "mobile";
  const swatches = config.swatches || [];
  const gallery = config.gallery || [];
  const videos = config.videos || [];

  const setSections = (list) => setDoc({ ...doc, sections: list });
  const setTheme = (patch) => setDoc({ ...doc, site: { ...site, theme: { ...theme, ...patch } } });
  const setSite = (patch) => setDoc({ ...doc, site: { ...site, ...patch } });

  const addSection = (type) => {
    const s = { id: nextId(), type, ...BLANK, ...(PRESET[type] || {}) };
    setSections([...sections, s]);
    setSelId(s.id);
  };
  const patchSection = (id, patch) => setSections(sections.map((s) => (s.id === id ? { ...s, ...patch } : s)));
  const move = (id, dir) => {
    const i = sections.findIndex((s) => s.id === id);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= sections.length) return;
    const out = [...sections];
    [out[i], out[j]] = [out[j], out[i]];
    setSections(out);
  };
  const dropOn = (id) => {
    if (!dragId || dragId === id) return;
    const from = sections.findIndex((s) => s.id === dragId);
    const to = sections.findIndex((s) => s.id === id);
    if (from < 0 || to < 0) return;
    const out = [...sections];
    const [it] = out.splice(from, 1);
    out.splice(to, 0, it);
    setSections(out);
    setDragId(null);
  };
  const remove = (id) => { setSections(sections.filter((s) => s.id !== id)); setSelId(null); };
  const duplicate = (id) => {
    const i = sections.findIndex((s) => s.id === id);
    if (i < 0) return;
    const copy = { ...sections[i], id: nextId() };
    const out = [...sections];
    out.splice(i + 1, 0, copy);
    setSections(out);
  };

  const loadTemplate = (id) => {
    const t = (config.templates || []).find((x) => x.id === id);
    if (!t) return;
    setDoc({ ...doc, site: { ...site, template: id }, sections: JSON.parse(JSON.stringify(t.sections || [])).map((s) => ({ ...BLANK, ...s })) });
    setSelId(null);
    toast.success(id === "blank" ? "Cleared the page." : `Loaded the ${t.name} template — now make it yours.`);
  };

  const aiAssist = async () => {
    setAiLoading(true);
    try {
      const r = await api.post("/ai/design-suggest", {
        site_name: doc.name || "",
        topic: sections.map((s) => `${s.type}: ${s.heading || ""} ${s.body || ""}`).join(" | ").slice(0, 900),
      });
      setDoc({ ...doc, aiUsed: true, aiSuggestion: r.data });
      toast.success("AI Assist has suggestions — read them, then decide what to keep.");
    } catch (e) {
      toast.error("AI Assist is unavailable right now. Try again in a moment.");
    } finally { setAiLoading(false); }
  };

  const applyAi = () => {
    const s = doc.aiSuggestion || {};
    setDoc({
      ...doc, aiApplied: true,
      site: { ...site, theme: { ...theme, ...(s.accent ? { accent: s.accent } : {}), ...(s.bg ? { bg: s.bg } : {}), ...(s.text ? { text: s.text } : {}) } },
    });
    toast.success("Applied the AI palette — you can still change any color yourself.");
  };

  const navLinks = site.navLinks || [];

  const field = (label, testid, value, onChange, placeholder, rows) => (
    <label className="block">
      <span className="text-[10px] uppercase tracking-widest text-slate-400">{label}</span>
      {rows ? (
        <textarea data-testid={testid} rows={rows} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
          className="mt-1 w-full rounded-md bg-slate-800 border border-slate-600 px-2 py-1.5 text-xs text-white placeholder:text-slate-500 outline-none focus:border-[#22D3EE] resize-none" />
      ) : (
        <input data-testid={testid} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
          className="mt-1 w-full h-8 rounded-md bg-slate-800 border border-slate-600 px-2 text-xs text-white placeholder:text-slate-500 outline-none focus:border-[#22D3EE]" />
      )}
    </label>
  );

  const editor = (s) => (
    <div data-testid={`site-editor-${s.id}`} className="bg-slate-900 border-t-2 border-[#22D3EE] p-3 grid sm:grid-cols-2 gap-3">
      {s.type !== "footer" && field("Heading", `site-heading-${s.id}`, s.heading, (v) => patchSection(s.id, { heading: v }), "Section heading")}
      {["hero", "text", "footer", "video", "career", "cta"].includes(s.type) && field(s.type === "video" ? "Why this video helps" : "Body text", `site-body-${s.id}`, s.body, (v) => patchSection(s.id, { body: v }), "Write a sentence or two…", 3)}
      {s.type === "cta" && field("Button text", `site-button-${s.id}`, s.buttonText, (v) => patchSection(s.id, { buttonText: v }), "Join the club")}
      {MEDIA(s.type) && (
        <>
          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-slate-400">Picture</span>
            <select data-testid={`site-image-${s.id}`} value={s.imageId || ""} onChange={(e) => patchSection(s.id, { imageId: e.target.value })}
              className="mt-1 w-full h-8 rounded-md bg-slate-800 border border-slate-600 px-1.5 text-xs text-white outline-none">
              <option value="">Choose a picture…</option>
              {gallery.map((g) => <option key={g.id} value={g.id}>{g.label}</option>)}
            </select>
          </label>
          {field("Alt text", `site-alt-${s.id}`, s.alt, (v) => patchSection(s.id, { alt: v }), "Describe the picture")}
          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-slate-400">License</span>
            <select data-testid={`site-license-${s.id}`} value={s.license || ""} onChange={(e) => patchSection(s.id, { license: e.target.value })}
              className="mt-1 w-full h-8 rounded-md bg-slate-800 border border-slate-600 px-1.5 text-xs text-white outline-none">
              <option value="">Choose a license…</option>
              {(config.licenses || []).map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
          </label>
          {field("Credit the creator", `site-credit-${s.id}`, s.credit, (v) => patchSection(s.id, { credit: v }), "Photo by … / Wikimedia Commons")}
          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-slate-400">File size for the web</span>
            <select data-testid={`site-quality-${s.id}`} value={s.quality || "full"} onChange={(e) => patchSection(s.id, { quality: e.target.value })}
              className="mt-1 w-full h-8 rounded-md bg-slate-800 border border-slate-600 px-1.5 text-xs text-white outline-none">
              {(config.imageQuality || []).map((q) => <option key={q.id} value={q.id}>{q.name}</option>)}
            </select>
          </label>
        </>
      )}
      {s.type === "video" && (
        <label className="block">
          <span className="text-[10px] uppercase tracking-widest text-slate-400">Video clip</span>
          <select data-testid={`site-video-${s.id}`} value={s.videoId || ""} onChange={(e) => patchSection(s.id, { videoId: e.target.value })}
            className="mt-1 w-full h-8 rounded-md bg-slate-800 border border-slate-600 px-1.5 text-xs text-white outline-none">
            <option value="">Choose a clip…</option>
            {videos.map((v) => <option key={v.id} value={v.id}>{v.label}</option>)}
          </select>
        </label>
      )}
      {s.type === "career" && (
        <>
          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-slate-400">Career</span>
            <select data-testid={`site-career-${s.id}`} value={s.careerName || ""} onChange={(e) => patchSection(s.id, { careerName: e.target.value })}
              className="mt-1 w-full h-8 rounded-md bg-slate-800 border border-slate-600 px-1.5 text-xs text-white outline-none">
              <option value="">Choose a career…</option>
              {(config.careers || []).map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
          {field("Typical wages", `site-wage-${s.id}`, s.wage, (v) => patchSection(s.id, { wage: v }), "e.g. $48,000–$72,000 a year")}
          {field("Education / training", `site-education-${s.id}`, s.education, (v) => patchSection(s.id, { education: v }), "e.g. 2-year degree + portfolio")}
          {field("Skills needed", `site-skills-${s.id}`, s.skills, (v) => patchSection(s.id, { skills: v }), "List the skills this job needs…", 2)}
        </>
      )}
      {s.type === "cards" && (
        <div className="sm:col-span-2 grid sm:grid-cols-3 gap-2">
          {(s.cards || []).map((c, i) => (
            <div key={i} className="space-y-1.5">
              <input data-testid={`site-card-title-${s.id}-${i}`} value={c.title || ""} onChange={(e) => patchSection(s.id, { cards: s.cards.map((x, j) => (j === i ? { ...x, title: e.target.value } : x)) })}
                placeholder={`Card ${i + 1} title`} className="w-full h-8 rounded-md bg-slate-800 border border-slate-600 px-2 text-xs text-white placeholder:text-slate-500 outline-none focus:border-[#22D3EE]" />
              <textarea data-testid={`site-card-body-${s.id}-${i}`} rows={2} value={c.body || ""} onChange={(e) => patchSection(s.id, { cards: s.cards.map((x, j) => (j === i ? { ...x, body: e.target.value } : x)) })}
                placeholder="One short sentence" className="w-full rounded-md bg-slate-800 border border-slate-600 px-2 py-1.5 text-xs text-white placeholder:text-slate-500 outline-none resize-none focus:border-[#22D3EE]" />
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const render = (s) => <SiteSectionView s={s} theme={theme} navLinks={navLinks} gallery={gallery} videos={videos} />;

  return (
    <div className="rounded-2xl overflow-hidden border border-white/10">
      <div className="bg-slate-950/90 border-b border-white/10 px-2 py-1.5 flex flex-wrap items-center gap-1.5">
        <select data-testid="site-template" value={site.template || "blank"} onChange={(e) => loadTemplate(e.target.value)}
          className="h-8 rounded-md bg-slate-800 border border-slate-600 px-1.5 text-xs text-white outline-none">
          {(config.templates || []).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
        <ColorField testid="site-bg" label="Background" value={theme.bg} onChange={(v) => setTheme({ bg: v })} swatches={swatches} />
        <ColorField testid="site-accent" label="Accent" value={theme.accent} onChange={(v) => setTheme({ accent: v })} swatches={swatches} />
        <ColorField testid="site-text" label="Text" value={theme.text} onChange={(v) => setTheme({ text: v })} swatches={swatches} />
        <select data-testid="site-font" value={theme.font || "Arial"} onChange={(e) => setTheme({ font: e.target.value })}
          className="h-8 rounded-md bg-slate-800 border border-slate-600 px-1.5 text-xs text-white outline-none">
          {(config.webFonts || []).map((f) => <option key={f} value={f}>{f}</option>)}
        </select>
        <input data-testid="site-nav-links" value={navLinks.join(", ")} onChange={(e) => setSite({ navLinks: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) })}
          placeholder="Nav links: Home, About, Contact" className="h-8 w-52 rounded-md bg-slate-800 border border-slate-600 px-2 text-xs text-white placeholder:text-slate-500 outline-none focus:border-[#22D3EE]" />
        <button data-testid="site-preview-desktop" onClick={() => setDoc({ ...doc, preview: "desktop" })}
          className={`h-8 px-2 rounded-md inline-flex items-center gap-1 text-xs ${!mobile ? "bg-[#22D3EE] text-[#04121f]" : "text-slate-200 hover:bg-white/10"}`}><Monitor className="w-3.5 h-3.5" /> Desktop</button>
        <button data-testid="site-preview-mobile" onClick={() => setDoc({ ...doc, preview: "mobile", previewedMobile: true })}
          className={`h-8 px-2 rounded-md inline-flex items-center gap-1 text-xs ${mobile ? "bg-[#22D3EE] text-[#04121f]" : "text-slate-200 hover:bg-white/10"}`}><Smartphone className="w-3.5 h-3.5" /> Mobile</button>
        <button data-testid="site-ai-assist" onClick={aiAssist} disabled={aiLoading}
          className="h-8 px-2 rounded-md inline-flex items-center gap-1 text-xs bg-[#a5b4fc]/20 text-[#c7d2fe] hover:bg-[#a5b4fc]/30 disabled:opacity-50">
          {aiLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />} AI Assist
        </button>
        <input data-testid="site-name-input" value={doc.name || ""} onChange={(e) => setDoc({ ...doc, name: e.target.value })} placeholder="Untitled site"
          className="ml-auto h-8 w-40 rounded-md bg-slate-800 border border-slate-600 px-2 text-xs text-white placeholder:text-slate-400 outline-none focus:border-[#22D3EE]" />
      </div>

      <div className="bg-slate-950/70 border-b border-white/10 px-2 py-1.5 flex flex-wrap items-center gap-1">
        <span className="text-[10px] uppercase tracking-widest text-slate-400 px-1 inline-flex items-center gap-1"><Layout className="w-3 h-3" /> Add block</span>
        {(config.sectionTypes || []).map((t) => (
          <button key={t.id} data-testid={`site-add-${t.id}`} draggable onDragStart={() => setDragId(null)} onClick={() => addSection(t.id)}
            className="h-7 px-2 rounded-md text-[11px] text-slate-200 hover:bg-white/10 inline-flex items-center gap-1"><Plus className="w-3 h-3" /> {t.name}</button>
        ))}
      </div>

      {doc.aiSuggestion && (
        <div data-testid="site-ai-panel" className="bg-[#1e1b4b]/60 border-b border-[#a5b4fc]/30 px-3 py-2.5">
          <p className="text-[10px] uppercase tracking-widest text-[#c7d2fe] mb-1">AI Assist · suggestions (you decide)</p>
          <div className="flex flex-wrap items-center gap-2 text-xs text-slate-200">
            {doc.aiSuggestion.accent && <span className="inline-flex items-center gap-1.5">Accent <span className="w-4 h-4 rounded border border-white/40" style={{ background: doc.aiSuggestion.accent }} /> <code className="font-mono">{doc.aiSuggestion.accent}</code></span>}
            {doc.aiSuggestion.headline && <span>· Headline idea: “{doc.aiSuggestion.headline}”</span>}
            <button data-testid="site-ai-apply" onClick={applyAi} className="h-7 px-2 rounded-md bg-[#a5b4fc] text-[#04121f] text-[11px] font-medium">Apply the palette</button>
          </div>
          {(doc.aiSuggestion.tips || []).map((t, i) => <p key={i} className="text-[11px] text-slate-300 mt-1">• {t}</p>)}
        </div>
      )}

      <div className="bg-slate-300 p-3 sm:p-5">
        <div ref={pageRef} data-testid="site-canvas" className={`mx-auto shadow-2xl overflow-hidden ${mobile ? "w-[380px]" : "w-full"}`}
          style={{ background: theme.bg || "#FFFFFF", color: theme.text || "#0F172A", fontFamily: theme.font || "Arial" }}>
          {sections.map((s, i) => (
            <div key={s.id} data-testid={`site-section-${s.id}`}
              draggable onDragStart={() => setDragId(s.id)} onDragOver={(e) => e.preventDefault()} onDrop={() => dropOn(s.id)}
              onClick={() => setSelId(selId === s.id ? null : s.id)}
              className={`relative group cursor-pointer ${selId === s.id ? "ring-2 ring-[#22D3EE] ring-inset" : ""} ${dragId === s.id ? "opacity-50" : ""}`}>
              <div className="absolute right-1 top-1 z-10 flex items-center gap-0.5 bg-slate-900/85 rounded-md px-1 py-0.5 opacity-60 hover:opacity-100 transition-opacity">
                <GripVertical className="w-3.5 h-3.5 text-slate-400" title="Drag to reorder" />
                <button data-testid={`site-up-${s.id}`} onClick={(e) => { e.stopPropagation(); move(s.id, -1); }} className="p-1 text-slate-200 hover:text-[#22D3EE]" title="Move up"><ArrowUp className="w-3.5 h-3.5" /></button>
                <button data-testid={`site-down-${s.id}`} onClick={(e) => { e.stopPropagation(); move(s.id, 1); }} className="p-1 text-slate-200 hover:text-[#22D3EE]" title="Move down"><ArrowDown className="w-3.5 h-3.5" /></button>
                <button data-testid={`site-dup-${s.id}`} onClick={(e) => { e.stopPropagation(); duplicate(s.id); }} className="p-1 text-slate-200 hover:text-[#22D3EE]" title="Duplicate"><Copy className="w-3.5 h-3.5" /></button>
                <button data-testid={`site-del-${s.id}`} onClick={(e) => { e.stopPropagation(); remove(s.id); }} className="p-1 text-slate-200 hover:text-[#f43f5e]" title="Delete"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
              <span className="absolute left-1 top-1 z-10 text-[10px] bg-slate-900/85 text-slate-300 rounded px-1.5 py-0.5 opacity-60">{i + 1}. {s.type}</span>
              {render(s)}
              {selId === s.id && <div onClick={(e) => e.stopPropagation()}>{editor(s)}</div>}
            </div>
          ))}
          {sections.length === 0 && (
            <p data-testid="site-empty-hint" className="py-20 text-center text-sm text-slate-500">Add blocks above (or load a template) to build your page.</p>
          )}
        </div>
        <div className="mt-3 bg-white rounded-lg border border-slate-300 p-3 max-w-3xl mx-auto">
          <p className="text-xs font-medium text-slate-500 mb-1">Design notes — your choices, what you optimized, and your reflections</p>
          <textarea data-testid="site-notes-input" value={doc.notes || ""} onChange={(e) => setDoc({ ...doc, notes: e.target.value })} rows={2}
            placeholder="Explain your layout, colors and any AI or copyright decisions…"
            className="w-full bg-transparent outline-none resize-none text-sm text-slate-800 placeholder:text-slate-400" />
        </div>
      </div>
    </div>
  );
}
